import { clickGokartOffer } from './api';
import { isMobileUserAgent } from '../components/OfferDetailModal';

/**
 * Runs the GoKart click handshake and opens the resulting url, mirroring the
 * Besitos open UX: mobile → new tab; desktop → QR.
 *
 * Popup-blocker safe: on mobile we open a BLANK tab SYNCHRONOUSLY (before the
 * `await`) so it stays inside the tap gesture, then navigate it once the
 * handshake resolves. If `window.open` is blocked (returns null), we fall back
 * to same-tab navigation via `location.assign` — the click is NEVER silently
 * dropped. Desktop awaits the handshake first (no gesture constraint) and hands
 * the url to the QR callback.
 *
 * Callbacks are page-owned UI: `onQr(url, title)` shows the desktop QR modal,
 * `onMessage(msg)` shows the unavailable / try-again notice.
 */
export async function openGokartClick(
  offerId: string,
  title: string,
  cb: { onQr: (url: string, title: string) => void; onMessage: (msg: string) => void },
): Promise<void> {
  const mobile = isMobileUserAgent();

  // Mobile only: claim the popup synchronously, inside the gesture, pre-await.
  const tab = mobile ? window.open('', '_blank', 'noopener,noreferrer') : null;

  let res;
  try {
    res = await clickGokartOffer(offerId);
  } catch {
    tab?.close();
    cb.onMessage('Something went wrong — please try again.');
    return;
  }

  if (res.available && res.click_url) {
    if (mobile) {
      if (tab) {
        tab.location.href = res.click_url;
      } else {
        // Popup blocked — fall back to same-tab navigation, never drop the click.
        window.location.assign(res.click_url);
      }
    } else {
      cb.onQr(res.click_url, title);
    }
    return;
  }

  // Unavailable / retry — close the blank tab and surface the message.
  tab?.close();
  cb.onMessage(res.message ?? (res.retry ? 'Please try again in a moment.' : 'This offer is not available right now.'));
}
